import { useContext, useEffect, type FC } from 'react';
import { Public } from './Public';
import { Private } from './Private';
import { TermsOfServiceChecker } from './TermsOfServiceChecker';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { PrivateApolloProvider } from '@/providers/PrivateApolloProvider';
import { PublicApolloProvider } from '@matrix-forge/shared';
import { UserPrefsProvider } from '@matrix-forge/ui/contexts/UserPrefsContext';
import { FeatureFlagsProvider } from '@matrix-forge/ui/contexts/FeatureFlagsContext';
import { AppToaster } from './AppToaster';
import { useUserDetailsWithFeatures } from '@/hooks/useUserDetailsWithFeatures';
import { publicClient, markSessionAuthenticated } from '@/utils/apollo';
import { StateMessage } from '@matrix-forge/ui/custom/StateMessage';

/**
 * Component that fetches user features after authentication.
 * Must be inside PrivateApolloProvider to access GraphQL.
 */
const FeaturesEnhancer: FC = () => {
  useUserDetailsWithFeatures(); // Fetch and update features
  return null; // This component doesn't render anything
};

/**
 * Checks if we have info for the current end-user. If so, assume we have an authenticated
 * user and we can render private user-specific components; otherwise render public content only.
 */
export const UserInfoChecker: FC = () => {
  const { userInfo, isLoading } = useContext(UserInfoContext);

  useEffect(() => {
    if (userInfo?.id) {
      markSessionAuthenticated();
    }
  }, [userInfo?.id]);

  // Show loading while checking for existing session (waiting for GraphQL query to complete)
  if (isLoading) {
    return <StateMessage isLoading title="Loading..." />;
  }

  // With cookie-based auth, we just need to check if userInfo exists (has id)
  // The session cookie handles authentication for GraphQL requests
  return userInfo?.id ? (
    <UserPrefsProvider key={userInfo.id} userId={userInfo.id}>
      <PrivateApolloProvider>
        <FeaturesEnhancer />
        <FeatureFlagsProvider features={userInfo?.features ?? {}}>
          <ThemeProvider>
            <TermsOfServiceChecker key={userInfo.id}>
              <Private key={userInfo.id} />
            </TermsOfServiceChecker>
            <AppToaster />
          </ThemeProvider>
        </FeatureFlagsProvider>
      </PrivateApolloProvider>
    </UserPrefsProvider>
  ) : (
    <PublicApolloProvider client={publicClient}>
      <Public />
    </PublicApolloProvider>
  );
};
